import React, { useState, useEffect } from 'react';
import { distributorService, mediaService } from '../../services/api';
import { Plus, Search, Edit2, Trash2, X, Map, Phone, Mail, CheckCircle, AlertCircle, User, Globe, Briefcase, Image as ImageIcon, Eye, ExternalLink, Percent, ShieldCheck } from 'lucide-react';

function DistributorsManagement() {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewingDistributor, setViewingDistributor] = useState(null);
  const [editingDistributor, setEditingDistributor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    logo: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    region: '',
    territory: '',
    commission_rate: '',
    credit_limit: '',
    type: 'distributor',
    status: 'pending'
  });

  useEffect(() => {
    loadDistributors();
  }, []);

  const loadDistributors = async () => {
    setLoading(true);
    try {
      const data = await distributorService.index();
      setDistributors(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error loading distributors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDistributor) {
        await distributorService.update(editingDistributor.id, formData);
      } else {
        await distributorService.store(formData);
      }
      closeModal();
      loadDistributors();
    } catch (error) {
      alert('Error saving distributor: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const res = await mediaService.upload(file, 'distributors');
      setFormData(prev => ({ ...prev, logo: res.url }));
    } catch (error) {
      console.error('Logo upload error:', error);
      alert('Failed to upload logo.');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (distributor) => {
    setEditingDistributor(distributor);
    setFormData({
      company_name: distributor.company_name || '',
      logo: distributor.logo || '',
      contact_person: distributor.contact_person || '',
      email: distributor.email || '',
      phone: distributor.phone || '',
      address: distributor.address || '',
      city: distributor.city || '',
      state: distributor.state || '',
      postal_code: distributor.postal_code || '',
      region: distributor.region || '',
      territory: distributor.territory || '',
      commission_rate: distributor.commission_rate || '',
      credit_limit: distributor.credit_limit || '',
      type: distributor.type || 'distributor',
      status: distributor.status || 'pending'
    });
    setShowModal(true);
  };

  const handleView = (distributor) => {
    setViewingDistributor(distributor);
    setShowDetailModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this distributor? This will remove all associated network data.')) {
      try {
        await distributorService.destroy(id);
        loadDistributors();
      } catch (error) {
        console.error('Error deleting distributor:', error);
      }
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      if (status === 'active') {
        await distributorService.approve(id);
      } else {
        await distributorService.suspend(id);
      }
      loadDistributors();
    } catch (error) {
      alert('Error updating status: ' + (error.response?.data?.message || error.message));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDistributor(null);
    setFormData({ 
      company_name: '', 
      logo: '',
      contact_person: '', 
      email: '', 
      phone: '', 
      address: '', 
      city: '', 
      state: '', 
      postal_code: '', 
      region: '', 
      territory: '', 
      commission_rate: '',
      credit_limit: '',
      type: 'distributor',
      status: 'pending' 
    });
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setViewingDistributor(null);
  };

  const filteredDistributors = distributors.filter(d => 
    d.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.region?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="text-slate-400 animate-pulse">Mapping global distribution network...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Distribution Partners</h2>
          <p className="text-sm text-slate-500 mt-1">Manage regional distributors and territory allocations.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center justify-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-red-500 transition-all shadow-lg shadow-red-900/20"
        >
          <Plus size={18} />
          Add New Partner
        </button>
      </div>

      <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-800 bg-slate-900/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              placeholder="Search by partner name, email, or region..."
              className="w-full bg-slate-950 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50">
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Partner Info</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Geography</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Contact</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Status</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredDistributors.map((partner) => (
                <tr key={partner.id} className="hover:bg-slate-800/30 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {partner.logo ? (
                        <img src={partner.logo} alt={partner.company_name} className="h-10 w-10 rounded-xl object-cover border border-slate-700 bg-white" />
                      ) : (
                        <div className="h-10 w-10 rounded-xl bg-slate-800 flex items-center justify-center text-red-500 font-bold border border-slate-700">
                          <Globe size={18} />
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-slate-200">{partner.company_name}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider font-black mt-0.5">
                          ID: #{partner.id.toString().padStart(4, '0')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm text-slate-300 flex items-center gap-2">
                        <Map size={12} className="text-red-500" />
                        {partner.region}
                      </div>
                      <div className="text-xs text-slate-500">
                        {partner.territory}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm text-slate-300 flex items-center gap-2 font-mono">
                        <Mail size={12} className="text-slate-500" />
                        {partner.email}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        <Phone size={12} className="text-slate-500" />
                        {partner.phone || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${
                      partner.status === 'active' ? 'bg-red-500/10 text-red-500' : 
                      partner.status === 'pending' ? 'bg-amber-500/10 text-amber-500' :
                      'bg-red-500/10 text-red-500'
                    }`}>
                      {partner.status === 'active' ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                      {partner.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {partner.status === 'pending' && (
                        <button
                          onClick={() => handleStatusChange(partner.id, 'active')}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                          title="Approve Partner"
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      {partner.status === 'active' && (
                        <button
                          onClick={() => handleStatusChange(partner.id, 'suspended')}
                          className="p-2 text-amber-500 hover:bg-amber-500/10 rounded-lg transition-all"
                          title="Suspend Partner"
                        >
                          <AlertCircle size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => handleView(partner)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="View Partner Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleEdit(partner)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Edit Partner"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(partner.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Delete Partner"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredDistributors.length === 0 && (
            <div className="text-center py-20">
              <div className="bg-slate-800 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 text-slate-600">
                <Globe size={32} />
              </div>
              <p className="text-slate-500 font-medium">No partners found matching your search.</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
              <h3 className="text-lg font-bold text-slate-100">
                {editingDistributor ? 'Edit Partner Details' : 'Register New Partner'}
              </h3>
              <button onClick={closeModal} className="text-slate-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[85vh]">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                {/* Logo & Basic Info */}
                <div className="md:col-span-4 space-y-6">
                  <div className="relative group mx-auto w-32">
                    <div className="h-32 w-32 rounded-[2rem] bg-slate-950 border-2 border-dashed border-slate-800 flex items-center justify-center overflow-hidden">
                      {formData.logo ? (
                        <img src={formData.logo} alt="Logo preview" className="h-full w-full object-cover" />
                      ) : (
                        <ImageIcon size={32} className="text-slate-700" />
                      )}
                      {uploading && (
                        <div className="absolute inset-0 bg-slate-950/80 flex items-center justify-center">
                          <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={handleLogoUpload}
                      accept="image/*"
                    />
                    <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mt-3 text-center">Company Identity</div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Partner Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['distributor', 'super_distributor'].map(t => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setFormData({ ...formData, type: t })}
                          className={`py-2 rounded-xl text-[9px] uppercase font-black tracking-widest border transition-all ${
                            formData.type === t 
                              ? 'bg-red-500/10 border-red-500 text-red-500'
                              : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'
                          }`}
                        >
                          {t.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Primary Details */}
                <div className="md:col-span-8 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Company Legal Name</label>
                      <div className="relative">
                        <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input
                          type="text"
                          required
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                          value={formData.company_name}
                          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                          placeholder="Ortus Global Pvt Ltd"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Primary Contact Person</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input
                          type="text"
                          required
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                          value={formData.contact_person}
                          onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                          placeholder="John Carter"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Business Email</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input
                          type="email"
                          required
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-mono"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="billing@company.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Contact Number</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input
                          type="tel"
                          required
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-mono"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-800">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">City</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g. London"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">State / Province</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="e.g. Ontario"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Postal Code</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="X0X 0X0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 pt-2">
                <div className="md:col-span-8 space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Full Physical Address</label>
                  <textarea
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors resize-none"
                    rows={2}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street name, Building number..."
                  />
                </div>
                <div className="md:col-span-4 space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Territory ID</label>
                  <input
                    type="text"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.territory}
                    onChange={(e) => setFormData({ ...formData, territory: e.target.value })}
                    placeholder="CAL-WEST-01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-800">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Region Assigned</label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                    <input
                      type="text"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                      value={formData.region}
                      onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      placeholder="e.g. Asia Pacific"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Commission (%)</label>
                  <div className="relative">
                    <Percent className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                    <input
                      type="number"
                      step="0.01"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                      value={formData.commission_rate}
                      onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                      placeholder="5.00"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Credit Limit (₹)</label>
                  <input
                    type="number"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                    placeholder="10000"
                  />
                </div>
              </div>

              <div className="space-y-1.5 pt-4 border-t border-slate-800">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Network Operational Status</label>
                <div className="flex gap-4">
                  {['pending', 'active', 'suspended'].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFormData({ ...formData, status: s })}
                      className={`flex-1 py-3 rounded-xl text-[10px] uppercase font-black tracking-widest border transition-all ${
                        formData.status === s 
                          ? s === 'active' ? 'bg-red-500/10 border-red-500 text-red-500' : 
                            s === 'pending' ? 'bg-amber-500/10 border-amber-500 text-amber-500' :
                            'bg-red-500/10 border-red-500 text-red-500'
                          : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-8 py-3 rounded-xl text-sm font-semibold text-slate-400 hover:text-white transition-colors"
                >
                  Discard Changes
                </button>
                <button
                  type="submit"
                  className="px-12 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-500 transition-all shadow-lg shadow-red-900/20"
                >
                  {editingDistributor ? 'Update Network Partner' : 'Confirm Registration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailModal && viewingDistributor && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-[110] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="relative h-48 bg-gradient-to-br from-red-600 to-red-900">
              <button 
                onClick={closeDetailModal} 
                className="absolute top-6 right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all z-10"
              >
                <X size={20} />
              </button>
              
              <div className="absolute -bottom-12 left-10">
                <div className="h-32 w-32 rounded-[2rem] bg-white p-1 shadow-2xl border-4 border-slate-900">
                  {viewingDistributor.logo ? (
                    <img src={viewingDistributor.logo} alt={viewingDistributor.company_name} className="h-full w-full object-cover rounded-[1.75rem]" />
                  ) : (
                    <div className="h-full w-full bg-slate-100 rounded-[1.75rem] flex items-center justify-center text-red-600 text-4xl font-black">
                      {viewingDistributor.company_name?.charAt(0)}
                    </div>
                  )}
                </div>
              </div>

              <div className="absolute top-10 left-10 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-[10px] font-black uppercase tracking-[0.2em]">
                {viewingDistributor.type.replace('_', ' ')} Partner
              </div>
            </div>

            <div className="px-10 pt-16 pb-10 space-y-8">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-3xl font-black text-white">{viewingDistributor.company_name}</h3>
                  <div className="flex items-center gap-2 mt-2 text-red-500 font-bold">
                    <Globe size={16} />
                    <span>{viewingDistributor.region} &bull; {viewingDistributor.territory}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Approved At</div>
                  <div className="text-slate-200 text-sm font-bold">
                    {viewingDistributor.approved_at ? new Date(viewingDistributor.approved_at).toLocaleDateString() : 'Pending Approval'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-2">Office Address</label>
                    <p className="text-slate-300 text-sm leading-relaxed">{viewingDistributor.address}, {viewingDistributor.city}, {viewingDistributor.state}, {viewingDistributor.postal_code}</p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-2">Network Profile</label>
                    <div className="flex items-center gap-3">
                      <div className="px-3 py-1 bg-slate-800 rounded-lg text-xs font-mono text-red-400">#DIS-{viewingDistributor.id.toString().padStart(5, '0')}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-2">Contact Officer</label>
                    <div className="space-y-2">
                      <div className="text-white font-bold text-sm">{viewingDistributor.contact_person}</div>
                      <div className="flex items-center gap-3 text-slate-300 text-sm">
                        <Mail size={14} className="text-slate-500" />
                        {viewingDistributor.email}
                      </div>
                      <div className="flex items-center gap-3 text-slate-300 text-sm font-mono">
                        <Phone size={14} className="text-slate-500" />
                        {viewingDistributor.phone}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-1">Commission</label>
                      <div className="text-red-500 font-black text-lg">{viewingDistributor.commission_rate}%</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-1">Credit Limit</label>
                      <div className="text-white font-black text-lg">₹{viewingDistributor.credit_limit}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black tracking-widest uppercase ${
                    viewingDistributor.status === 'active' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'
                  }`}>
                    {viewingDistributor.status}
                  </span>
                  {viewingDistributor.parent && (
                    <span className="text-[10px] text-slate-500 font-bold ml-4 uppercase">Subsidiary of {viewingDistributor.parent.company_name}</span>
                  )}
                </div>
                <button 
                  onClick={() => { setShowDetailModal(false); handleEdit(viewingDistributor); }}
                  className="text-slate-400 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                >
                  Update Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DistributorsManagement;

